<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Stack, Input, Text } from '@Shelly/shelly-design-system';
	import TokenSelector from '../Shared/TokenSelector.svelte';
	import { selectDeviceConditions } from '../../../Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/common-helpers';
	import { toSceneOperator } from '../../converters/helpers/device-conditions';

	/**
	 * DeviceConditionEditor — purpose-built, wizard-free editor for device conditions.
	 *
	 * Design principles:
	 * - Receives all data as props; never reads from Svelte stores directly.
	 * - Emits a single `change` event with the full updated _device_condition object.
	 * - No reactive writes to parent state; no bind: on any store or complex object.
	 * - Safe to mount/unmount without side effects.
	 */

	/** Array of condition component objects returned by _init_conditions_components() */
	export let components: any[] = [];

	/** Current _device_condition object from node.data */
	export let condition: any = {};

	const dispatch = createEventDispatcher<{ change: any }>();

	// ── Derived selectors ──────────────────────────────────────────────────────

	/** Flat list of {title, value, disabled} for the property dropdown. */
	$: propertyOptions = selectDeviceConditions(components);

	/**
	 * The combined property option-value currently active.
	 * Mirrors the logic in selectDeviceConditions() so the Select shows the right
	 * item even when condition was loaded from saved data.
	 */
	$: currentPropValue =
		condition.add_info_ind !== undefined && condition.add_info_ind !== null
			? `${condition.cond_name}${condition.add_info_ind}`
			: condition.input_index !== undefined && condition.input_index !== null
				? `${condition.cond_name}${condition.input_index}`
				: condition.cond_name || '';

	/** Full option object for Select display (same pattern as InlineDeviceSelector). */
	$: selectedPropertyOption = currentPropValue
		? (propertyOptions.find((o) => o.value === currentPropValue) ?? {
				title: currentPropValue,
				value: currentPropValue,
			})
		: undefined;

	/** The component object that matches the currently selected property. */
	$: selectedComponent =
		components.find((el) => {
			if (!currentPropValue) return false;
			const elValue =
				typeof el.props.add_info_ind !== 'undefined'
					? `${el.props.id}${el.props.add_info_ind}`
					: typeof el.props.input_index !== 'undefined'
						? `${el.props.id}${el.props.input_index}`
						: el.props.id;
			return elValue === currentPropValue;
		}) || null;

	/** Whether the selected property has an enum/state value list. */
	$: hasValues = selectedComponent?.props?.values?.length > 0;

	/** Whether the selected property requires a numeric threshold. */
	$: hasNumericInput =
		selectedComponent !== null && typeof selectedComponent?.props?.input_value !== 'undefined';

	/** Whether the selected property requires a text input. */
	$: hasTextInput =
		selectedComponent !== null && typeof selectedComponent?.props?.input_text !== 'undefined';

	/** Whether the selected property uses key-value pairs. */
	$: hasKeyValues = selectedComponent?.props?.key_values?.length > 0;

	/** Same as Scenes wizard: operator dropdown + numeric threshold (temperature, roller %, …). */
	$: isThresholdProperty = Boolean(hasValues && hasNumericInput);

	/**
	 * Enum/select option list: threshold props use underscored values like DevicePropsStep
	 * (`less_than`); plain enums keep raw values (`on`, `off`, …).
	 */
	$: valueOptions = hasValues
		? (selectedComponent.props.values as string[]).map((v: string) =>
				isThresholdProperty
					? {
							value: v.toLowerCase().replace(/\s+/g, '_'),
							title: formatConditionValue(v),
						}
					: { value: v, title: formatConditionValue(v) },
			)
		: [];

	$: currentEnumOrThresholdKey = isThresholdProperty
		? toOperatorKey(condition.operator)
		: String(condition.value ?? '');

	$: selectedValueOption =
		hasValues && valueOptions.length > 0 && currentEnumOrThresholdKey
			? (valueOptions.find((o) =>
					isThresholdProperty
						? operatorMatchesThresholdOption(o.value, String(condition.operator ?? ''))
						: o.value === currentEnumOrThresholdKey ||
							toOperatorKey(o.value) === toOperatorKey(currentEnumOrThresholdKey),
				) ?? {
					title: formatConditionValue(currentEnumOrThresholdKey),
					value: currentEnumOrThresholdKey,
				})
			: undefined;

	/** Key-value options for key_values type conditions. */
	$: keyValueOptions = hasKeyValues
		? (selectedComponent.props.key_values as any[]).map((kv: any) => ({
				value: kv.key ?? kv.value ?? kv,
				title:
					typeof kv.label !== 'undefined'
						? kv.label
						: formatConditionValue(String(kv.key ?? kv.value ?? kv)),
			}))
		: [];

	$: currentKeyValue = String(condition.operator ?? '');

	$: selectedKeyValueOption =
		hasKeyValues && keyValueOptions.length > 0 && currentKeyValue
			? (keyValueOptions.find((o) => o.value === currentKeyValue) ?? {
					title: currentKeyValue,
					value: currentKeyValue,
				})
			: undefined;

	/** Preselect first enum/state when value is missing or not in the list (not for threshold + numeric). */
	$: if (
		hasValues &&
		!hasNumericInput &&
		valueOptions.length > 0 &&
		selectedComponent &&
		!valueOptions.some((o) => o.value === currentEnumOrThresholdKey)
	) {
		const firstVal = valueOptions[0]?.value;
		if (firstVal !== undefined && String(firstVal) !== '') {
			applyEnumValueByOption(String(firstVal));
		}
	}

	/** Default first comparison operator when threshold property has no valid operator yet. */
	$: if (
		hasValues &&
		hasNumericInput &&
		valueOptions.length > 0 &&
		selectedComponent &&
		!valueOptions.some((o) =>
			operatorMatchesThresholdOption(o.value, String(condition.operator ?? '')),
		)
	) {
		const first = valueOptions[0]?.value;
		if (first !== undefined && String(first) !== '') {
			applyThresholdOperator(String(first));
		}
	}

	/** Preselect first key-value when operator is missing or not in the list. */
	$: if (
		hasKeyValues &&
		keyValueOptions.length > 0 &&
		selectedComponent &&
		!keyValueOptions.some((o) => o.value === currentKeyValue)
	) {
		const firstK = keyValueOptions[0]?.value;
		if (firstK !== undefined && String(firstK) !== '') {
			applyKeyValueByOption(String(firstK));
		}
	}

	/** Unit character (e.g. '%', '°C', 'W'). */
	$: unit = selectedComponent?.props?.character || '';

	/** Numeric min/max constraints. */
	$: numMin = selectedComponent?.props?.min ?? 0;
	$: numMax = selectedComponent?.props?.max ?? 100;

	/** Numeric threshold display; fall back to component default when value empty or non-numeric (legacy bad merges). */
	$: numericFieldValue = (() => {
		if (!hasNumericInput) return String(condition.value ?? '0');
		const raw = condition.value;
		if (raw === '' || raw === undefined || raw === null) {
			return String(selectedComponent?.props?.input_value ?? 0);
		}
		const n = Number(raw);
		if (!Number.isFinite(n)) {
			return String(selectedComponent?.props?.input_value ?? 0);
		}
		return String(raw);
	})();

	// ── Helpers ────────────────────────────────────────────────────────────────

	function formatConditionValue(v: string): string {
		return v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	}

	function toOperatorKey(v: string): string {
		return String(v ?? '')
			.toLowerCase()
			.trim()
			.replace(/\s+/g, '_');
	}

	/** Scene JSON may use `gt` while capability options use `more_than`; avoid reactive resets & broken selects. */
	function operatorMatchesThresholdOption(optionValue: string, condOperator: string): boolean {
		const ov = toOperatorKey(optionValue);
		const cv = toOperatorKey(condOperator);
		if (cv === '' && !condOperator) return false;
		if (ov === cv) return true;
		return toSceneOperator(ov) === toSceneOperator(cv);
	}

	function extractInputValue(event: Event): string {
		const ce = event as CustomEvent<{ value?: unknown }>;
		if (
			ce.detail &&
			typeof ce.detail === 'object' &&
			'value' in ce.detail &&
			ce.detail.value !== undefined &&
			ce.detail.value !== null
		) {
			return String(ce.detail.value);
		}
		const t = event.target as HTMLInputElement | null;
		if (t?.value !== undefined && t?.value !== null) return String(t.value);
		return '';
	}

	function applyEnumValueByOption(selectedValue: string) {
		if (!selectedValue) return;
		dispatch('change', {
			...condition,
			value: selectedValue,
			operator: 'equal',
			cmpop: 'eq',
			cmpval: selectedValue,
		});
	}

	function applyKeyValueByOption(selectedValue: string) {
		if (!selectedValue) return;
		dispatch('change', { ...condition, operator: selectedValue });
	}

	function applyPropertyByOptionValue(selectedValue: string) {
		if (!selectedValue) return;

		const foundComp = components.find((el) => {
			const elValue =
				typeof el.props.add_info_ind !== 'undefined'
					? `${el.props.id}${el.props.add_info_ind}`
					: typeof el.props.input_index !== 'undefined'
						? `${el.props.id}${el.props.input_index}`
						: el.props.id;
			return elValue === selectedValue;
		});

		if (!foundComp) return;

		const defaultNum = foundComp.props.input_value;
		const nextValue = String(defaultNum !== undefined && defaultNum !== null ? defaultNum : '0');

		dispatch('change', {
			...condition,
			cond_name: foundComp.props.id,
			add_info_ind: foundComp.props.add_info_ind,
			input_index: foundComp.props.input_index,
			operator: '',
			value: nextValue,
			cmpop: 'eq',
			cmpval: nextValue,
		});
	}

	/** When a device has properties but none chosen yet, default to the first enabled option. */
	$: if (propertyOptions.length > 0 && !currentPropValue) {
		const first = propertyOptions.find((o) => !o.disabled) ?? propertyOptions[0];
		if (first?.value) {
			applyPropertyByOptionValue(first.value);
		}
	}

	// ── Event handlers ─────────────────────────────────────────────────────────

	function handlePropertyChange(event: CustomEvent) {
		const detail = event.detail;
		const selectedValue: string =
			typeof detail === 'string' ? detail : (detail?.value ?? detail ?? '');

		applyPropertyByOptionValue(selectedValue);
	}

	function handleOperatorChange(event: CustomEvent) {
		const detail = event.detail;
		const selectedValue: string =
			typeof detail === 'string' ? detail : (detail?.value ?? detail ?? '');

		dispatch('change', { ...condition, operator: selectedValue });
	}

	function applyThresholdOperator(operatorKey: string) {
		if (!operatorKey) return;
		const opKey = toOperatorKey(operatorKey);
		const cmpop = toSceneOperator(opKey);
		let threshold = condition.value ?? condition.cmpval ?? '0';
		if (!Number.isFinite(Number(threshold))) {
			threshold = String(selectedComponent?.props?.input_value ?? 0);
		}
		dispatch('change', {
			...condition,
			operator: opKey,
			cmpop,
			cmpval: threshold,
			value: String(threshold),
		});
	}

	/** Enum state (on/off) vs threshold operator (less_than / more_than), aligned with Scenes wizard. */
	function handleValueSelectChange(event: CustomEvent) {
		const detail = event.detail;
		const selectedValue: string =
			typeof detail === 'string' ? detail : (detail?.value ?? detail ?? '');

		if (isThresholdProperty) {
			applyThresholdOperator(selectedValue);
		} else {
			applyEnumValueByOption(selectedValue);
		}
	}

	function commitNumericOrTextValue(event: Event) {
		const newValue = extractInputValue(event);
		const cmpop = toSceneOperator(String(condition.operator ?? ''));
		dispatch('change', { ...condition, value: newValue, cmpval: newValue, cmpop });
	}

	function handleKeyValueChange(event: CustomEvent) {
		const detail = event.detail;
		const selectedValue: string =
			typeof detail === 'string' ? detail : (detail?.value ?? detail ?? '');

		applyKeyValueByOption(selectedValue);
	}
</script>

<Stack direction="row" gap="s">
	<!-- Property selector -->
	{#if propertyOptions.length > 0}
		<TokenSelector
			ariaLabel="Property"
			label={$format('flow_condition_property', { default: 'Property' })}
			options={propertyOptions}
			value={selectedPropertyOption}
			on:change={handlePropertyChange}
		/>
	{:else}
		<Text variant="body" size="s" style="color: #888; font-size: 0.8rem; padding: 0.5rem 0;">
			{$format('flow_condition_no_props', { default: 'No properties available for this device.' })}
		</Text>
	{/if}

	{#if selectedComponent}
		<!-- Enum / state value selection (e.g. on/off, open/close, less than/more than) -->
		{#if hasValues}
			<TokenSelector
				ariaLabel="Condition"
				label={$format('flow_condition_value', { default: 'Condition' })}
				options={valueOptions}
				value={selectedValueOption}
				on:change={handleValueSelectChange}
			/>
		{/if}

		<!-- Numeric threshold input (e.g. temperature > 25°C) -->
		{#if hasNumericInput}
			<Input
				aria-label="device-value-input"
				id="device-value-input"
				type="number"
				label="{$format('flow_condition_threshold', { default: 'Value' })}{unit
					? ` (${unit})`
					: ''}"
				value={numericFieldValue}
				min={String(numMin)}
				max={String(numMax)}
				on:input={commitNumericOrTextValue}
				on:change={commitNumericOrTextValue}
			/>
		{/if}

		<!-- Text input -->
		{#if hasTextInput}
			<Input
				aria-label="device-text-value-input"
				id="device-text-value-input"
				type="text"
				label={$format('flow_condition_value', { default: 'Value' })}
				value={String(condition.value ?? '')}
				on:input={commitNumericOrTextValue}
				on:change={commitNumericOrTextValue}
			/>
		{/if}

		<!-- Key-value selection -->
		{#if hasKeyValues}
			<TokenSelector
				ariaLabel="Value"
				label={$format('flow_condition_value', { default: 'Value' })}
				options={keyValueOptions}
				value={selectedKeyValueOption}
				on:change={handleKeyValueChange}
			/>
		{/if}
	{/if}
</Stack>

<style>
</style>
